import { fetchRequest } from "@campgladiator/cg-common.api.fetch-request";
import type {
  WorkoutDTO,
  Request,
  ProgramType,
  BaseEnumerableResponse,
  AuthToken,
} from "@campgladiator/cg-common.types.types";
import { getApiGatewayUrl } from "@campgladiator/cg-common.config.config";

export type FetchWorkoutsArgs = {
  includeDeleted?: boolean;
  page?: number;
  size?: number;
  trainerId?: string;
  type?: ProgramType;
};

export type CreateWorkoutArgs = {
  description: WorkoutDTO["description"];
  duration: WorkoutDTO["duration"];
  link: WorkoutDTO["link"];
  photo: WorkoutDTO["photo"];
  tags: WorkoutDTO["tags"];
  title: WorkoutDTO["title"];
  token: AuthToken["token"];
  trainerId: WorkoutDTO["trainerId"];
  trainerPhoto: string;
  type: WorkoutDTO["type"];
};

export const fetchWorkoutById = async (id: number): Promise<WorkoutDTO> => {
  try {
    const endpoint = `/library/v1/workouts/${id}`;
    const method = "GET";
    const request: Request = {
      requestConfig: {
        method,
        url: {
          baseUrl: getApiGatewayUrl(),
          endpoint,
        },
      },
    };
    const response = await fetchRequest({ request });
    const result = await response.json();

    return response.ok
      ? (result as WorkoutDTO)
      : Promise.reject(new Error(result.error));
  } catch (error) {
    let errorMessage = error instanceof Error ? error.message : error;
    errorMessage = errorMessage || "An unknown error occurred";

    throw new Error(errorMessage as string);
  }
};

export const fetchWorkouts = async ({
  includeDeleted,
  page,
  size,
  trainerId,
  type,
}: FetchWorkoutsArgs): Promise<BaseEnumerableResponse<WorkoutDTO>> => {
  const params = new URLSearchParams({
    ...(type && { type }),
    ...(size && { size: size.toString() }),
    ...(page && { page: page.toString() }),
    ...(trainerId && { trainerId }),
    ...(includeDeleted !== undefined && {
      includeDeleted: includeDeleted.toString(),
    }),
  });

  const endpoint = `/library/v1/workouts?${params.toString()}`;
  const method = "GET";

  try {
    const request: Request = {
      requestConfig: {
        method,
        url: {
          baseUrl: getApiGatewayUrl(),
          endpoint,
        },
      },
    };
    const response = await fetchRequest({ request });
    const data = await response.json();

    if (!response.ok) throw new Error(data);

    return data as Promise<BaseEnumerableResponse<WorkoutDTO>>;
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : String(error) || "An unknown error occurred";
    throw new Error(errorMessage);
  }
};

/**
 * Posts a new workout to the server.
 * @param description - The description of the workout.
 * @param duration - The duration tag of the workout.
 * @param link - The link to the workout video.
 * @param photo - The URL of the workout thumbnail.
 * @param tags - The tags associated with the workout.
 * @param title - The title of the workout.
 * @param token - The token to authenticate the request.
 * @param trainerId - The ID of the selected trainer.
 * @param trainerPhoto - The photo of the selected trainer.
 * @param type - The type of the workout.
 * @returns The created workout data.
 * @throws If an error occurs during the request process.
 */
export const createWorkout = async ({
  description,
  duration,
  link,
  photo,
  tags,
  title,
  token,
  trainerId,
  trainerPhoto,
  type,
}: CreateWorkoutArgs): Promise<BaseEnumerableResponse<WorkoutDTO>> => {
  const method = "POST";
  const body = JSON.stringify({
    description,
    duration,
    link,
    photo,
    tags,
    title,
    token,
    trainerId,
    trainerPhoto,
    type,
  });

  try {
    const request: Request = {
      requestConfig: {
        method,
        url: { baseUrl: getApiGatewayUrl(), endpoint: "/library/v1/workouts" },
        authToken: token,
      },
      body,
    };
    const response = await fetchRequest({ request });
    const data = await response.json();

    if (!response.ok) throw new Error(data);

    return data as Promise<BaseEnumerableResponse<WorkoutDTO>>;
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : String(error) || "An unknown error occurred";
    throw new Error(errorMessage);
  }
};
